// src/pages/DevNotes.js
import { useEffect, useState } from "react";
import { DevNotesAPI } from "../api/devNotes";
import { UsersAPI } from "../api/lookups";

export default function DevNotes() {
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState({
    type: "feature",
    category: "",
    title: "",
    details: "",
    priority: "medium",
    assigned_to_id: "",
  });

  const load = async () => {
    try {
      setLoading(true);
      const [n, s] = await Promise.all([
        DevNotesAPI.list({}),
        UsersAPI.list(),
      ]);
      setNotes(n);
      setUsers(s);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load Dev Notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (!form.category || !form.title || !form.details) return;

      const payload = {
        type: form.type,
        category: form.category,
        title: form.title,
        details: form.details,
        priority: form.priority,
        assigned_to_id: form.assigned_to_id || null,
      };

      await DevNotesAPI.create(payload);

      setForm({
        type: "feature",
        category: "",
        title: "",
        details: "",
        priority: "medium",
        assigned_to_id: "",
      });

      setShowModal(false);
      await load();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to create Dev Note");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await DevNotesAPI.update(id, { status });
      await load();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to update status");
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this Dev Note?")) return;
    try {
      await DevNotesAPI.delete(id);
      await load();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to delete Dev Note");
    }
  };

  const statusLabel = {
    open: "Open",
    in_progress: "In progress",
    resolved: "Resolved",
    won_t_fix: "Won't fix",
  };

  const priorityLabel = {
    low: "Low",
    medium: "Medium",
    high: "High",
    urgent: "Urgent",
  };

  return (
    <div className="p-4 h-[calc(100vh-80px)] flex flex-col gap-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Dev Notes</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded text-sm"
        >
          + Add Dev Note
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* List */}
      <div className="flex-1 overflow-y-auto border rounded p-4 bg-slate-50">
        {loading ? (
          <p>Loading Dev Notes...</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-slate-600">
            No Dev Notes yet. Click “Add Dev Note” to create one.
          </p>
        ) : (
          <ul className="space-y-2">
            {notes.map((n) => (
              <li
                key={n.id}
                className="border rounded p-3 flex flex-col gap-1 bg-white"
              >
                <div className="flex justify-between items-center gap-2">
                  <div>
                    <span className="text-xs uppercase bg-slate-100 px-2 py-0.5 rounded mr-2">
                      {n.type}
                    </span>
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded mr-2">
                      {n.category}
                    </span>
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                      {priorityLabel[n.priority] || n.priority}
                    </span>
                  </div>
                  <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                    {statusLabel[n.status] || n.status}
                  </span>
                </div>

                <div className="font-semibold text-sm">{n.title}</div>

                <div className="text-xs text-slate-700 whitespace-pre-wrap">
                  {n.details}
                </div>

                <div className="flex justify-between mt-1 text-xs text-slate-500">
                  <div>
                    Added{" "}
                    {n.added_at &&
                      new Date(n.added_at).toLocaleString()}
                    {n.resolved_at && (
                      <> · Resolved {new Date(n.resolved_at).toLocaleString()}</>
                    )}
                  </div>
                  <div>
                    {n.assigned_to_name
                      ? `Assigned: ${n.assigned_to_name}`
                      : "Unassigned"}
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-2 text-xs">
                  {n.status !== "open" && (
                    <button
                      onClick={() => updateStatus(n.id, "open")}
                      className="px-2 py-1 border rounded"
                    >
                      Re-open
                    </button>
                  )}
                  {n.status === "open" && (
                    <button
                      onClick={() => updateStatus(n.id, "in_progress")}
                      className="px-2 py-1 border rounded"
                    >
                      In progress
                    </button>
                  )}
                  {n.status !== "resolved" && (
                    <button
                      onClick={() => updateStatus(n.id, "resolved")}
                      className="px-2 py-1 border rounded bg-green-100"
                    >
                      Resolve
                    </button>
                  )}
                  <button
                    onClick={() => deleteNote(n.id)}
                    className="px-2 py-1 border rounded text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded shadow-lg w-full max-w-2xl p-6">
            <h2 className="text-lg font-semibold mb-4">
              Add Dev Note
            </h2>

            <form
              onSubmit={handleCreate}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div>
                <label className="block text-sm font-semibold mb-1">Type</label>
                <select
                  className="border p-2 w-full text-sm"
                  value={form.type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value }))
                  }
                >
                  <option value="feature">Feature</option>
                  <option value="bug">Bug</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Category</label>
                <input
                  className="border p-2 w-full text-sm"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1">Title</label>
                <input
                  className="border p-2 w-full text-sm"
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1">Details</label>
                <textarea
                  className="border p-2 w-full text-sm min-h-[80px]"
                  value={form.details}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, details: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Priority</label>
                <select
                  className="border p-2 w-full text-sm"
                  value={form.priority}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, priority: e.target.value }))
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Assigned to
                </label>
                <select
                  className="border p-2 w-full text-sm"
                  value={form.assigned_to_id}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      assigned_to_id: e.target.value,
                    }))
                  }
                >
                  <option value="">Unassigned</option>
                  {users.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm border rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded"
                >
                  Add Dev Note
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}